import _isObject from 'lodash/isObject';
import _flattenDeep from 'lodash/flattenDeep';

/**
 * This will safely read into the specified context object
 * to return the value at a nested path.
 *
 * NOTE: To access nested levels within an object you need
 *       to separate your path components with a `~` character
 *       since often times the attributes contain a `.`
 *       ex: parse({l1: {key: 'value'}}, 'l1~key') => 'value'
 *
 * @param {Object} context
 * @param {string} path
 * @return {*}
 */
export function parse(context = {}, path = '') {
  return readContext(context, path.split('~'));
}

function readContext(context = {}, pathParts) {
  return pathParts.length === 1
    ? context[pathParts[0]]
    : readContext(context[pathParts.shift()], pathParts);
}

/**
 * Recursively walks through the specified context object
 * looking for the first occurrence of an attribute with
 * the specified key. If found, it will return the full
 * path required to access that (potentially) nested attribute.
 *
 * NOTE: The character separator for nested attributes is `~`
 *        ex: parsePath({l1: {key: 'value'}}) => 'l1~key`
 *
 * @param {Object} context
 * @param {string} key
 * @returns {string}
 */
export function parsePath(context = {}, key) {
  if (!key) {
    return '';
  }

  return findKey(context, key, '');
}

function findKey(context = {}, key, path) {
  let result = '';

  for (const prop in context) {
    if (context.hasOwnProperty(prop)) {
      const value = context[prop];
      const resultFound = Boolean(result);

      if (!resultFound) {
        if (key === prop) {
          return path ? `${path}~${prop}` : prop;
        } else if (_isObject(value)) {
          result = findKey(value, key, path ? `${path}~${prop}` : prop);
        }
      }
    }
  }

  return result;
}

/**
 * This will safely assign the specified value to a copy
 * of the provided context object at the specified path.
 *
 * This allows us to safely assign values to a nested path
 * which may not exist initially on the context object.
 *
 * ex: assign({}, 'l1~l2~key', 'value') => {l1: {l2: {key: 'value'}}}
 *
 * @param context
 * @param path
 * @param value
 */
export function assign(context = {}, path = '', value) {
  const workingContext = JSON.parse(JSON.stringify(context));
  applyContext(workingContext, path.split('~'), value);

  return workingContext;
}

function applyContext(context = {}, pathParts, value) {
  const pathPart = pathParts.shift();

  if (pathPart) {
    if (pathParts.length) {
      context[pathPart] = context[pathPart] || {};
      applyContext(context[pathPart], pathParts, value);
    } else {
      context[pathPart] = value;
    }
  }
}

/**
 * This function is used to copy over some context from the
 * provided object into a new object which will contain
 * the specified attributes, and most importantly will copy
 * over the nested attributes of those keys if they already
 * existed on the context object.
 *
 * It's possible that this copy needs to take place on a
 * nested attribute of the context. This is where you can
 * specify the baseKey (which can be a key existing at any
 * nesting level within the context).
 *
 * @param context
 * @param attributes
 * @param baseKey
 */
export function copyContext(context = {}, attributes = [], baseKey) {
  const dependencyPath = baseKey ? parsePath(context, baseKey) : '';
  const dependencyBranch = dependencyPath
    ? parse(context, dependencyPath)
    : context;
  const newDependencyBranch = attributes.reduce((branch, attribute) => {
    if (attribute) {
      branch[attribute] = dependencyBranch[attribute] || {};
    }

    return branch;
  }, {});

  return assign(context, dependencyPath, newDependencyBranch);
}

/**
 * Recursively pulls out all of the keys within the
 * specified object into a single array.
 *
 * ex: flattenKeys({key1: { key3: { key4: {}}}, key2: {}}) => ['key1', 'key3', 'key4', 'key2']
 *
 * Note that the ordering is actually important here and will
 * match walking completely down each node before moving on to
 * the next root node.
 *
 * @param {object} object
 * @returns {Array}
 */
export function flattenKeys(object = {}) {
  return _flattenDeep(
    Object.keys(object).reduce((keys, key) => {
      keys.push(key);

      if (_isObject(object[key])) {
        keys.push(flattenKeys(object[key]));
      }

      return keys;
    }, [])
  );
}

/**
 * This performs the opposite task to `flattenKeys`
 * Given an array each element will turn into a key
 * within a new Object where it's value will be an
 * empty Object.
 *
 * ex: expandKeys(['key1', 'key2', 'key3']) => {key1:{}, key2: {}, key3: {}}
 *
 * @param {Array} arr
 * @returns {Object}
 */
export function expandKeys(arr = []) {
  return arr.reduce((result, key) => {
    result[key] = {};
    return result;
  }, {});
}
